/**
 * Maps customer move types to driver mobility attributes.
 *
 * Move types (from enriched customer config `allowed_move_types`) determine
 * what products a dealership has enabled. Mobility attributes determine what
 * a driver is qualified to do. This mapper bridges the two.
 *
 * Usage:
 *   const products = getEnabledProducts(['concierge', 'concierge-loaner']);
 *   // → Set { 'mobility_valet' }
 */

export type MobilityAttribute =
  | 'mobility_valet'
  | 'mobility_shuttle'
  | 'mobility_service'
  | 'mobility_parts';

/**
 * Maps each move type to the mobility attributes it enables.
 * A single move type can enable multiple attributes, and multiple
 * move types can enable the same attribute.
 */
const MOVE_TYPE_TO_ATTRIBUTES: Record<string, MobilityAttribute[]> = {
  // Valet moves (concierge rebrand → valet)
  'concierge': ['mobility_valet'],
  'concierge-loaner': ['mobility_valet'],
  // Operational moves (business-to-business, same capability as valet)
  'one-way': ['mobility_valet'],
  'round-trip': ['mobility_valet'],
  // Future mappings:
  // 'shuttle': ['mobility_shuttle'],
  // 'service-pickup': ['mobility_service'],
  // 'parts-delivery': ['mobility_parts'],
};

/** Attribute display metadata */
export interface AttributeMeta {
  key: MobilityAttribute;
  label: string;
  description: string;
}

export const ATTRIBUTE_META: Record<MobilityAttribute, AttributeMeta> = {
  mobility_valet: {
    key: 'mobility_valet',
    label: 'Valet',
    description: 'Can perform valet and operational moves',
  },
  mobility_shuttle: {
    key: 'mobility_shuttle',
    label: 'Shuttle',
    description: 'Can perform shuttle transport services',
  },
  mobility_service: {
    key: 'mobility_service',
    label: 'Mobile Service',
    description: 'Can perform mobile service appointments',
  },
  mobility_parts: {
    key: 'mobility_parts',
    label: 'Parts',
    description: 'Can perform parts pickup and delivery',
  },
};

/**
 * Given a flat list of allowed move types (union of all customer configs),
 * return the set of mobility attributes those move types enable.
 */
export function getEnabledProducts(allMoveTypes: string[]): Set<MobilityAttribute> {
  const enabled = new Set<MobilityAttribute>();
  for (const mt of allMoveTypes) {
    const attrs = MOVE_TYPE_TO_ATTRIBUTES[mt];
    if (attrs) {
      for (const a of attrs) enabled.add(a);
    }
  }
  return enabled;
}

/**
 * Extract allowed_move_types from an enriched customer config.
 * Returns an empty array if not present.
 */
export function extractMoveTypes(enrichedConfig: unknown): string[] {
  if (!enrichedConfig || typeof enrichedConfig !== 'object') return [];
  const config = enrichedConfig as Record<string, unknown>;
  if (Array.isArray(config.allowed_move_types)) {
    return config.allowed_move_types as string[];
  }
  return [];
}

/**
 * Attributes granted by service flags rather than move types. Mobile Service
 * is enabled via `config.mobile_service.active` — it never appears in
 * `allowed_move_types`, which is why it can't live in MOVE_TYPE_TO_ATTRIBUTES.
 * Same enablement key the portal's driver-management feature gate uses. Reads
 * config rather than the portal products because the config may belong to any
 * of a driver's rooftops, and the portal-products snapshot covers the working
 * rooftop only.
 */
export function getServiceProducts(enrichedConfig: unknown): MobilityAttribute[] {
  if (!enrichedConfig || typeof enrichedConfig !== 'object') return [];
  const config = enrichedConfig as Record<string, any>;
  return config.mobile_service?.active === true ? ['mobility_service'] : [];
}

/** One rooftop's inputs to attribute derivation: its tier + enriched config. */
export interface RooftopProductSource {
  tier?: string | null;
  enrichedConfig: unknown | null;
}

/**
 * Full attribute derivation for a driver's rooftops: move-type products
 * (union of allowed_move_types) plus service-flag products (see
 * getServiceProducts). Null/failed enrichments are skipped. This is THE
 * entry point for "which attributes can this driver have" — UserDriverTab,
 * DriverAttributesTab, and the Add Team Member wizard all derive from it.
 *
 * Move-type products only count from LITE rooftops: on lite/SaaS rooftops
 * the dealership's own drivers perform the moves, while on standard
 * (full-service) rooftops HopDrive drivers do — and since the hopdrive
 * default config carries allowed_move_types ("one-way", "round-trip"),
 * every rooftop inherits valet move types through enrichment. Without the
 * tier gate, Valet would show for every driver everywhere. Service-flag
 * products (Mobile Service) are tier-independent.
 */
export function getEnabledProductsFromConfigs(
  sources: RooftopProductSource[],
): Set<MobilityAttribute> {
  const moveTypes: string[] = [];
  const enabled = new Set<MobilityAttribute>();
  for (const { tier, enrichedConfig } of sources) {
    if (!enrichedConfig) continue;
    if (tier === 'lite') moveTypes.push(...extractMoveTypes(enrichedConfig));
    for (const p of getServiceProducts(enrichedConfig)) enabled.add(p);
  }
  for (const p of getEnabledProducts([...new Set(moveTypes)])) enabled.add(p);
  return enabled;
}
